/**
 * Bulk-resolve CSV taxonomy (config/topic/subtopic) for ALL questions at once.
 * Avoids per-page mount resolution that left off-screen questions invalid until visited.
 */
import courseTopicService from "./course-topic-service";
import courseSubTopicService from "./course-sub-topic-service";

export function normalizeTaxonomyName(str) {
  return String(str || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    // Curly/smart quotes → straight
    .replace(/[\u2018\u2019\u201A\u201B\uFFFD]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    // Em/en dashes → hyphen
    .replace(/[\u2013\u2014]/g, "-")
    // Non-breaking / odd spaces
    .replace(/[\u00A0\u2000-\u200B]/g, " ")
    // Collapse whitespace
    .replace(/\s+/g, " ");
}

function splitPipeList(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object" && item.name) return String(item.name).trim();
        return String(item).trim();
      })
      .filter(Boolean);
  }
  if (!value || typeof value !== "string") return [];
  return value
    .split("|")
    .map((v) => v.trim())
    .filter(Boolean);
}

function hasObjectTaxonomy(list) {
  return (
    Array.isArray(list) &&
    list.length > 0 &&
    typeof list[0] === "object" &&
    list[0] &&
    list[0]._id
  );
}

function matchByNames(pool, names) {
  if (!pool || !pool.length || !names.length) return [];
  const normalized = names.map(normalizeTaxonomyName);
  return pool.filter(
    (item) => item && item.name && normalized.includes(normalizeTaxonomyName(item.name)),
  );
}

function collectTopicIds(questions) {
  const ids = new Set();
  (questions || []).forEach((q) => {
    if (!hasObjectTaxonomy(q.topics)) return;
    q.topics.forEach((t) => {
      if (t && t._id) ids.add(String(t._id));
    });
  });
  return [...ids];
}

function collectConfigIds(questions, configPool) {
  const ids = new Set();
  (questions || []).forEach((q) => {
    if (hasObjectTaxonomy(q.configurations)) {
      q.configurations.forEach((c) => {
        if (c && c._id) ids.add(String(c._id));
      });
    }
  });
  (configPool || []).forEach((c) => {
    if (c && c._id) ids.add(String(c._id));
  });
  return [...ids];
}

/**
 * Resolve missing taxonomy objects on every question in one pass.
 * Mutates questions in place. Returns count of questions updated.
 */
export async function bulkResolveCsvTaxonomy(questions, {
  configurations = [],
  topics = [],
  subtopics = [],
} = {}) {
  if (!Array.isArray(questions) || !questions.length) return 0;

  // Fast path: everything already resolved as objects (no extra API calls)
  const needsResolve = questions.some(
    (q) =>
      !hasObjectTaxonomy(q.configurations) ||
      !hasObjectTaxonomy(q.topics) ||
      !hasObjectTaxonomy(q.subtopics),
  );
  if (!needsResolve) return 0;

  let configPool = [...(configurations || [])];
  let topicPool = [...(topics || [])];
  let subtopicPool = [...(subtopics || [])];

  // Expand topic pool from configuration IDs already known on questions / configs
  const configIds = collectConfigIds(questions, configPool);
  if (configIds.length) {
    try {
      const response = await courseTopicService.getAllTopics(configIds, "", "", 1, 500);
      const docs = (response && response.data && response.data.docs) || [];
      const byId = new Map(topicPool.map((t) => [String(t._id), t]));
      docs.forEach((t) => {
        if (t && t._id) byId.set(String(t._id), t);
      });
      topicPool = [...byId.values()];
    } catch (e) {
      // Keep existing pool — name match may still work for already-loaded topics
      console.warn("bulkResolveCsvTaxonomy: topic fetch failed", e);
    }
  }

  // Resolve topic names → objects first (needed to fetch subtopics)
  questions.forEach((q) => {
    if (hasObjectTaxonomy(q.topics)) return;
    const raw =
      (typeof q.csv_topics === "string" && q.csv_topics) ||
      (typeof q.topics === "string" && q.topics) ||
      (typeof q.topic === "string" && q.topic) ||
      "";
    const names = splitPipeList(raw);
    if (!names.length) return;
    const matched = matchByNames(topicPool, names);
    if (matched.length) {
      q.topics = matched;
    }
  });

  // One subtopic fetch for all topic IDs across the quiz
  const topicIds = collectTopicIds(questions);
  if (topicIds.length) {
    try {
      const response = await courseSubTopicService.getAllSubTopics(topicIds, "", 1, 2000);
      const docs = (response && response.data && response.data.docs) || [];
      const byId = new Map(subtopicPool.map((s) => [String(s._id), s]));
      docs.forEach((s) => {
        if (s && s._id) byId.set(String(s._id), s);
      });
      subtopicPool = [...byId.values()];
    } catch (e) {
      console.warn("bulkResolveCsvTaxonomy: subtopic fetch failed", e);
    }
  }

  let updated = 0;
  questions.forEach((q) => {
    let changed = false;

    // Configurations
    if (!hasObjectTaxonomy(q.configurations)) {
      const raw =
        (typeof q.csv_configurations === "string" && q.csv_configurations) ||
        (typeof q.configurations === "string" && q.configurations) ||
        (typeof q.configuration === "string" && q.configuration) ||
        "";
      const names = splitPipeList(raw);
      const matched = matchByNames(configPool, names);
      if (matched.length) {
        q.configurations = matched;
        changed = true;
      }
    }

    // Topics (second pass if pool grew)
    if (!hasObjectTaxonomy(q.topics)) {
      const raw =
        (typeof q.csv_topics === "string" && q.csv_topics) ||
        (typeof q.topics === "string" && q.topics) ||
        "";
      const names = splitPipeList(raw);
      const matched = matchByNames(topicPool, names);
      if (matched.length) {
        q.topics = matched;
        changed = true;
      }
    }

    // Subtopics — prefer raw csv / sub_topics string (names may contain commas)
    if (!hasObjectTaxonomy(q.subtopics)) {
      const raw =
        (typeof q.csv_sub_topics === "string" && q.csv_sub_topics) ||
        (typeof q.sub_topics === "string" && q.sub_topics) ||
        (typeof q["sub-topics"] === "string" && q["sub-topics"]) ||
        (typeof q.subtopics === "string" && q.subtopics) ||
        "";
      const names = splitPipeList(raw);
      let matched = matchByNames(subtopicPool, names);

      // If topics are known, prefer subtopics under those topics
      if (matched.length && hasObjectTaxonomy(q.topics)) {
        const topicIdSet = new Set(q.topics.map((t) => String(t._id)));
        const scoped = matched.filter(
          (s) => s.topic_id && topicIdSet.has(String(s.topic_id)),
        );
        if (scoped.length) matched = scoped;
      }

      if (matched.length) {
        q.subtopics = matched;
        changed = true;

        // Derive parent topics from subtopics when topics still missing
        if (!hasObjectTaxonomy(q.topics)) {
          const parentIds = [
            ...new Set(matched.map((s) => s.topic_id).filter(Boolean).map(String)),
          ];
          const derived = topicPool.filter((t) => parentIds.includes(String(t._id)));
          if (derived.length) {
            q.topics = derived;
          }
        }

        // Derive configs from topics
        if (hasObjectTaxonomy(q.topics) && !hasObjectTaxonomy(q.configurations)) {
          const parentConfigIds = [
            ...new Set(
              q.topics
                .map((t) => t.configuration_id)
                .filter(Boolean)
                .map(String),
            ),
          ];
          const derivedConfigs = configPool.filter((c) =>
            parentConfigIds.includes(String(c._id)),
          );
          if (derivedConfigs.length) {
            q.configurations = derivedConfigs;
          }
        }
      }
    }

    if (changed) updated += 1;
  });

  return updated;
}
